import { Injectable, inject } from '@angular/core';

import { SuiModalService } from '@semeris/ui';
import { EMPTY, from, Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';

import { SystemMessageDismissalService } from './system-message-dismissal.service';
import { SystemMessage, SystemMessageType } from '../models/system-message.model';

@Injectable({ providedIn: 'root' })
export class SystemMessageModalService {
  private readonly modalService = inject(SuiModalService);
  private readonly dismissalService = inject(SystemMessageDismissalService);

  /**
   * Opens the `modal` messages the user has not dismissed, one at a time in the given order, and completes
   * once they have all closed. A message that cannot be closed holds back the ones after it.
   */
  public showPending(messages: SystemMessage[]): Observable<void> {
    const dismissed = this.dismissalService.dismissed();
    const pending = messages.filter(
      (message) => message.type === SystemMessageType.Modal && !(message.closable && dismissed.has(message.id)),
    );
    if (!pending.length) {
      return EMPTY;
    }

    return from(import('../components/system-message-modal/system-message-modal.component')).pipe(
      switchMap(({ SystemMessageModalComponent }) =>
        from(pending).pipe(
          concatMap((message) =>
            this.modalService
              .open(SystemMessageModalComponent, {
                data: message,
                closeOnEscape: message.closable,
                closeOnBackdropClick: message.closable,
              })
              .closed$.pipe(map(() => this.onClosed(message))),
          ),
        ),
      ),
    );
  }

  private onClosed(message: SystemMessage): void {
    if (message.closable) {
      this.dismissalService.dismiss(message.id);
    }
  }
}
